import { AR_CANVAS_HEIGHT, AR_CANVAS_WIDTH, type ARConfig, type ARModule } from './types';
import { devLog } from '../lib/devLog';

/**
 * Stand-in for the PixelChefs module so the whole app is runnable before their
 * code exists. It implements the contract and nothing else:
 *
 * - raw camera feed to a 720p portrait canvas
 * - a crude placeholder shape when the costume is visible (not art)
 * - spacebar fires onTrigger, so the flow is testable without face tracking
 * - every interface call is logged
 *
 * No face tracking, no landmarks, no MediaPipe. That is all inside the vendor
 * module and must never appear here.
 */

const TAG = '[ar:stub]';

function log(call: string, ...rest: unknown[]): void {
  devLog(`${TAG} ${call}`, ...rest);
}

export function createStubModule(): ARModule {
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let video: HTMLVideoElement | null = null;
  let stream: MediaStream | null = null;
  let rafId: number | null = null;
  let running = false;
  let costumeVisible = false;
  let cfg: ARConfig | null = null;
  let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
  let destroyed = false;

  function drawCoverFit(v: HTMLVideoElement, c: CanvasRenderingContext2D): void {
    const vw = v.videoWidth || AR_CANVAS_WIDTH;
    const vh = v.videoHeight || AR_CANVAS_HEIGHT;
    const scale = Math.max(AR_CANVAS_WIDTH / vw, AR_CANVAS_HEIGHT / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    const dx = (AR_CANVAS_WIDTH - dw) / 2;
    const dy = (AR_CANVAS_HEIGHT - dh) / 2;

    // Mirror, so the guest sees themselves the way a mirror shows them.
    c.save();
    c.translate(AR_CANVAS_WIDTH, 0);
    c.scale(-1, 1);
    c.drawImage(v, AR_CANVAS_WIDTH - dx - dw, dy, dw, dh);
    c.restore();
  }

  /** Deliberately crude: a straw shawl block and a beaked mask. Placeholder. */
  function drawPlaceholderCostume(c: CanvasRenderingContext2D): void {
    const cx = AR_CANVAS_WIDTH / 2;
    const headY = AR_CANVAS_HEIGHT * 0.36;

    // Shawl
    c.fillStyle = 'rgba(200, 162, 74, 0.85)';
    c.beginPath();
    c.moveTo(cx - 260, AR_CANVAS_HEIGHT);
    c.lineTo(cx - 150, headY + 150);
    c.lineTo(cx + 150, headY + 150);
    c.lineTo(cx + 260, AR_CANVAS_HEIGHT);
    c.closePath();
    c.fill();

    // Straw texture, such as it is
    c.strokeStyle = 'rgba(90, 70, 20, 0.5)';
    c.lineWidth = 3;
    for (let i = -240; i <= 240; i += 24) {
      c.beginPath();
      c.moveTo(cx + i * 0.6, headY + 160);
      c.lineTo(cx + i, AR_CANVAS_HEIGHT);
      c.stroke();
    }

    // Mask
    c.fillStyle = 'rgba(232, 226, 212, 0.9)';
    c.beginPath();
    c.ellipse(cx, headY, 130, 165, 0, 0, Math.PI * 2);
    c.fill();

    // Beak
    c.fillStyle = 'rgba(125, 31, 26, 0.95)';
    c.beginPath();
    c.moveTo(cx, headY + 10);
    c.lineTo(cx - 26, headY + 60);
    c.lineTo(cx + 26, headY + 60);
    c.closePath();
    c.fill();

    // Eyes
    c.fillStyle = '#0b0c0a';
    c.beginPath();
    c.ellipse(cx - 52, headY - 30, 20, 26, 0, 0, Math.PI * 2);
    c.ellipse(cx + 52, headY - 30, 20, 26, 0, 0, Math.PI * 2);
    c.fill();
  }

  function drawStubBadge(c: CanvasRenderingContext2D): void {
    // Canvas text inherits nothing from CSS, so the Tailwind stack has to be
    // repeated by hand here — same families, same order as fontFamily.body.
    c.font = '600 22px Helvetica, Arial, Roboto, sans-serif';
    c.fillStyle = 'rgba(232, 226, 212, 0.55)';
    c.textAlign = 'center';
    c.fillText('STUB AR MODULE — SPACEBAR TO TRIGGER', AR_CANVAS_WIDTH / 2, 40);
    c.textAlign = 'start';
  }

  function frame(): void {
    if (!running || !ctx || !video) return;
    ctx.fillStyle = '#0b0c0a';
    ctx.fillRect(0, 0, AR_CANVAS_WIDTH, AR_CANVAS_HEIGHT);

    if (video.readyState >= 2) drawCoverFit(video, ctx);
    if (costumeVisible) drawPlaceholderCostume(ctx);
    drawStubBadge(ctx);

    rafId = requestAnimationFrame(frame);
  }

  return {
    async init(container: HTMLElement, config: ARConfig): Promise<void> {
      log('init', { deviceId: config.deviceId ?? '(default)' });
      cfg = config;
      destroyed = false;

      canvas = document.createElement('canvas');
      canvas.width = AR_CANVAS_WIDTH;
      canvas.height = AR_CANVAS_HEIGHT;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.objectFit = 'cover';
      canvas.style.display = 'block';
      container.appendChild(canvas);
      ctx = canvas.getContext('2d', { alpha: false });

      onKeyDown = (e: KeyboardEvent) => {
        if (e.code !== 'Space') return;
        e.preventDefault();
        log('onTrigger (spacebar)');
        cfg?.onTrigger();
      };
      window.addEventListener('keydown', onKeyDown);

      try {
        // Video only. The module never touches the microphone.
        stream = await navigator.mediaDevices.getUserMedia({
          video: config.deviceId
            ? { deviceId: { exact: config.deviceId }, width: 1280, height: 720 }
            : { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        log('onError', e.message);
        config.onError(e);
        return;
      }

      // A destroy() that landed while getUserMedia was in flight.
      if (destroyed) {
        stream.getTracks().forEach((t) => t.stop());
        stream = null;
        return;
      }

      video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play().catch((err: unknown) => {
        const e = err instanceof Error ? err : new Error(String(err));
        log('onError (play)', e.message);
        config.onError(e);
      });

      log('onReady');
      config.onReady();
    },

    start(): void {
      log('start');
      if (running) return;
      running = true;
      rafId = requestAnimationFrame(frame);
    },

    stop(): void {
      log('stop');
      running = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    },

    setCostumeVisible(visible: boolean): void {
      log('setCostumeVisible', visible);
      costumeVisible = visible;
    },

    getCanvas(): HTMLCanvasElement {
      log('getCanvas');
      if (!canvas) throw new Error('AR stub: getCanvas() before init()');
      return canvas;
    },

    destroy(): void {
      log('destroy');
      destroyed = true;
      running = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (onKeyDown) {
        window.removeEventListener('keydown', onKeyDown);
        onKeyDown = null;
      }
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
      if (video) {
        video.srcObject = null;
        video = null;
      }
      canvas?.parentElement?.removeChild(canvas);
      canvas = null;
      ctx = null;
      cfg = null;
      costumeVisible = false;
    },
  };
}
